<script>
import baseMixin from '@/mixins/baseMixin'
import { mapGetters, mapActions } from 'vuex'
import DateFormatToken from '@/constants/date/DateFormatToken'
import dayjs from '@/services/date/index'
import { filterDuplicateAdminNodes } from '@/helpers/org-tree-helpers'
import TruncatedChipTooltip from '../truncated-chip-tooltip.vue'

export default {
  name: 'ManagePresetsTable',
  components: {
    TruncatedChipTooltip,
  },
  mixins: [baseMixin],
  model: {
    prop: 'selectedPreset',
    event: 'change',
  },
  props: {
    selectedPreset: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters({
      presets: 'orgTree/presets',
      getUserNameById: 'users/getUserNameById',
    }),
    tableHeaders() {
      return [
        {
          text: 'Title',
          align: 'start',
          value: 'title',
        },
        { text: 'Selection', value: 'selection', align: 'center' },
        { text: 'Favourite', value: 'favourite', align: 'center' },
        { text: 'Created', value: 'createdDate' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ]
    },
  },
  methods: {
    ...mapActions({
      togglePresetFavouriteState: 'orgTree/togglePresetFavouriteState',
    }),
    formatPresetCreatedDate(date) {
      return dayjs(date).format(DateFormatToken.LLLL)
    },
    handleSelection(value) {
      this.$emit('change', value)
    },
    editPreset(key) {
      this.$emit('edit', key)
    },
    deletePreset(key) {
      this.$emit('delete', key)
    },
    humanReadableDate(date) {
      if (!date) return ''

      return dayjs().to(dayjs(date))
    },
    removeDuplicateAdminsSelection(selection) {
      return filterDuplicateAdminNodes(selection)
    },
  },
}
</script>

<template>
  <div>
    <header>
      <h6 class="text-subtitle-1 font-weight-medium">{{
        $t('general.orgFilter.dialog.managePresets.title')
      }}</h6>
      <p class="text-caption">{{
        $t('general.orgFilter.dialog.managePresets.subtitle')
      }}</p>
    </header>

    <v-data-table
      :value="selectedPreset"
      :headers="tableHeaders"
      :items="presets"
      :class="['overflow-y-auto']"
      item-key="key"
      :search="search"
      show-select
      dense
      single-select
      selectable-key="key"
      :sort-desc="['favourite']"
      :sort-by="['favourite', 'title']"
      @input="handleSelection"
    >
      <template v-slot:item.selection="{ item }">
        <div class="d-flex justify-center align-center">
          <TruncatedChipTooltip
            :items="removeDuplicateAdminsSelection(item.selection)"
          >
            <template v-slot="{ onTooltip }">
              <v-chip small v-on="onTooltip">{{
                removeDuplicateAdminsSelection(item.selection).length
              }}</v-chip>
            </template>
          </TruncatedChipTooltip>
        </div>
      </template>

      <template v-slot:item.favourite="{ item }">
        <div class="d-flex justify-center align-center">
          <v-btn
            icon
            text
            :color="item.favourite ? 'warning' : null"
            @click="togglePresetFavouriteState(item.key)"
          >
            <v-icon>{{
              item.favourite ? 'mdi-star' : 'mdi-star-outline'
            }}</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:item.createdDate="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on: onTooltip }">
            <div class="d-flex align-center" v-on="onTooltip">
              {{ humanReadableDate(item.createdDate) }}
            </div>
          </template>
          {{ `Created on ${formatPresetCreatedDate(item.createdDate)}` }}
        </v-tooltip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-end align-center">
          <v-btn icon x-small class="mr-2" @click="editPreset(item.key)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>

          <v-btn icon x-small @click="deletePreset(item.key)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>
